import React, { useEffect, useMemo, useState } from 'react';
import { Plus, Edit3, Trash2, Save, ChevronLeft, Eye, EyeOff, Upload, FileText, Copy, Check } from 'lucide-react';
import api from '../api/client';

const emptyBlog = {
  title: '', slug: '', excerpt: '', category: 'Sleep & Rest', categoryEmoji: '🌙',
  categories: ['sleep'], language: 'English', readMinutes: 4,
  image: '', youtubeId: '', content: '', faqs: [{ q: '', a: '' }], published: true,
};

const CATEGORY_PRESETS = [
  { label: 'Sleep & Rest', emoji: '🌙', tag: 'sleep' },
  { label: 'Stress Relief', emoji: '🌿', tag: 'stress' },
  { label: 'Money Mindset', emoji: '✨', tag: 'money' },
  { label: 'हिंदी ध्यान', emoji: '🇮🇳', tag: 'hindi' },
  { label: 'Focus & Clarity', emoji: '🎯', tag: 'focus' },
  { label: 'Self-Love', emoji: '💚', tag: 'love' },
];

const slugify = (s) => (s || '').toLowerCase().trim()
  .replace(/[^a-z0-9\s-]/g, '')
  .replace(/\s+/g, '-').replace(/-+/g, '-');

const BlogManager = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // list | form
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyBlog);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [uploadingImg, setUploadingImg] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const [copiedKey, setCopiedKey] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setBlogs(await api.adminListBlogs()); }
    catch (_e) { setBlogs([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...emptyBlog, faqs: [{ q: '', a: '' }] });
    setView('form');
    setMsg('');
  };

  const openEdit = (b) => {
    setEditing(b);
    setForm({
      title: b.title || '', slug: b.slug || '', excerpt: b.excerpt || '',
      category: b.category || '', categoryEmoji: b.categoryEmoji || '',
      categories: b.categories || [], language: b.language || 'English',
      readMinutes: b.readMinutes || 4, image: b.image || '',
      youtubeId: b.youtubeId || '', content: b.content || '',
      faqs: b.faqs && b.faqs.length ? b.faqs : [{ q: '', a: '' }],
      published: b.published !== false,
    });
    setView('form');
    setMsg('');
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setMsg('');
    try {
      const payload = {
        ...form,
        faqs: form.faqs.filter((f) => f.q.trim() || f.a.trim()),
        slug: form.slug || slugify(form.title),
      };
      let updated;
      if (editing) updated = await api.updateBlog(editing.id, payload);
      else updated = await api.createBlog(payload);
      setMsg(editing ? 'Blog updated.' : 'Blog published live.');
      await load();
      setEditing(null);
      setForm(emptyBlog);
      setView('list');
    } catch (err) {
      setMsg(err?.response?.data?.detail || 'Save failed. Check fields and try again.');
    } finally { setSaving(false); }
  };

  const togglePublished = async (b) => {
    try {
      const upd = await api.updateBlog(b.id, { published: !b.published });
      setBlogs((list) => list.map((x) => x.id === upd.id ? upd : x));
    } catch (_e) { /* ignore */ }
  };

  const remove = async (b) => {
    if (!window.confirm(`Delete "${b.title}"? This cannot be undone.`)) return;
    try { await api.deleteBlog(b.id); setBlogs((list) => list.filter((x) => x.id !== b.id)); } catch (_e) { /* ignore */ }
  };

  const handleImageUpload = (file) => {
    setUploadErr('');
    if (!file) return;
    if (!file.type.startsWith('image/')) { setUploadErr('Please choose an image file.'); return; }
    if (file.size > 10 * 1024 * 1024) { setUploadErr('Image is too large (max 10MB).'); return; }
    setUploadingImg(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const maxSide = 1400;
        let { width, height } = img;
        if (width > height && width > maxSide) { height = Math.round((maxSide / width) * height); width = maxSide; }
        else if (height > maxSide) { width = Math.round((maxSide / height) * width); height = maxSide; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        let dataUrl = canvas.toDataURL('image/jpeg', 0.8);
        let quality = 0.8;
        while (dataUrl.length > 800 * 1024 && quality > 0.35) { quality -= 0.1; dataUrl = canvas.toDataURL('image/jpeg', quality); }
        if (dataUrl.length > 1.2 * 1024 * 1024) setUploadErr('Image is still too large after compression.');
        else setForm((f) => ({ ...f, image: dataUrl }));
        setUploadingImg(false);
      };
      img.onerror = () => { setUploadErr('Could not read image.'); setUploadingImg(false); };
      img.src = ev.target.result;
    };
    reader.onerror = () => { setUploadErr('Could not read file.'); setUploadingImg(false); };
    reader.readAsDataURL(file);
  };

  const setCategoryPreset = (p) => setForm((f) => ({ ...f, category: p.label, categoryEmoji: p.emoji, categories: [...new Set([...(f.categories || []), p.tag])] }));

  const toggleTag = (tag) => setForm((f) => {
    const s = new Set(f.categories || []);
    if (s.has(tag)) s.delete(tag); else s.add(tag);
    return { ...f, categories: Array.from(s) };
  });

  if (view === 'form') {
    return (
      <form onSubmit={save} className="space-y-4">
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { setEditing(null); setView('list'); }} className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"><ChevronLeft className="w-4 h-4" /> Back to list</button>
          <div className="ml-auto text-xs text-emerald-700">{editing ? `Editing: ${editing.title.slice(0, 40)}` : 'New blog post'}</div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-xs text-emerald-800 font-medium">Title *
            <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} onBlur={() => { if (!form.slug) setForm((f) => ({ ...f, slug: slugify(f.title) })); }} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
          </label>
          <label className="text-xs text-emerald-800 font-medium">Slug (URL)
            <input value={form.slug} onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder="auto-from-title" />
          </label>
        </div>

        <label className="block text-xs text-emerald-800 font-medium">Excerpt (1-2 sentences)
          <textarea rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
        </label>

        <div>
          <div className="text-xs text-emerald-800 font-medium mb-1">Category preset</div>
          <div className="flex flex-wrap gap-2">
            {CATEGORY_PRESETS.map((p) => (
              <button type="button" key={p.tag} onClick={() => setCategoryPreset(p)} className={`text-xs px-3 py-1.5 rounded-full border ${form.category === p.label ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-emerald-800 border-emerald-200 hover:bg-emerald-50'}`}>{p.emoji} {p.label}</button>
            ))}
          </div>
          <div className="mt-2 grid sm:grid-cols-3 gap-3">
            <label className="text-xs text-emerald-800 font-medium sm:col-span-2">Category label *
              <input required value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
            </label>
            <label className="text-xs text-emerald-800 font-medium">Emoji
              <input value={form.categoryEmoji} onChange={(e) => setForm({ ...form, categoryEmoji: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
            </label>
          </div>
        </div>

        <div>
          <div className="text-xs text-emerald-800 font-medium mb-1">Filter tags (which pills show this post)</div>
          <div className="flex flex-wrap gap-2">
            {['sleep', 'stress', 'money', 'hindi', 'focus', 'love'].map((t) => (
              <button type="button" key={t} onClick={() => toggleTag(t)} className={`text-xs px-3 py-1 rounded-full border ${form.categories.includes(t) ? 'bg-emerald-700 text-white border-emerald-700' : 'bg-white text-emerald-800 border-emerald-200'}`}>{t}</button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          <label className="text-xs text-emerald-800 font-medium">Language
            <select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 bg-white">
              <option>English</option>
              <option>Hindi</option>
            </select>
          </label>
          <label className="text-xs text-emerald-800 font-medium">Read time (min)
            <input type="number" min="1" max="60" value={form.readMinutes} onChange={(e) => setForm({ ...form, readMinutes: +e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" />
          </label>
          <label className="text-xs text-emerald-800 font-medium">YouTube ID or URL
            <input value={form.youtubeId} onChange={(e) => setForm({ ...form, youtubeId: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder="dQw4w9WgXcQ or full URL" />
          </label>
        </div>

        <div>
          <div className="text-xs text-emerald-800 font-medium mb-1">Cover Image</div>
          <div className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50/50 p-3">
            <div className="flex flex-wrap items-center gap-3">
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-700 text-white text-xs cursor-pointer hover:bg-emerald-800">
                <Upload className="w-3.5 h-3.5" /> {uploadingImg ? 'Processing…' : 'Upload cover image'}
                <input type="file" accept="image/*" className="hidden" disabled={uploadingImg} onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) handleImageUpload(f); e.target.value = ''; }} />
              </label>
              <span className="text-[11px] text-emerald-800/80">JPG/PNG · auto-resized to 1400px</span>
            </div>
            {uploadErr && <div className="mt-2 text-xs text-rose-600">{uploadErr}</div>}
          </div>
          <label className="block text-xs text-emerald-800 font-medium mt-2">Or Image URL
            <input value={form.image?.startsWith('data:') ? '' : form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200" placeholder={form.image?.startsWith('data:') ? 'Uploaded image in use' : 'https://...'} />
          </label>
          {form.image && (
            <div className="mt-2 relative w-full max-w-xs h-28 rounded-lg overflow-hidden border border-emerald-200">
              <img src={form.image} alt="Preview" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>
          )}
        </div>

        <label className="block text-xs text-emerald-800 font-medium">Content (Markdown-lite: use `## Heading` on its own line, blank line = new paragraph) *
          <textarea required rows={12} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-lg border border-emerald-200 font-mono text-[13px]" placeholder="Write your guide here..." />
        </label>

        <div>
          <div className="text-xs text-emerald-800 font-medium mb-1">FAQs (optional)</div>
          <div className="space-y-2">
            {form.faqs.map((f, i) => (
              <div key={i} className="rounded-lg border border-emerald-100 bg-white p-3 grid sm:grid-cols-[1fr_1fr_auto] gap-2">
                <input value={f.q} onChange={(e) => { const arr = [...form.faqs]; arr[i] = { ...arr[i], q: e.target.value }; setForm({ ...form, faqs: arr }); }} placeholder="Question" className="px-2 py-1.5 rounded border border-emerald-200 text-sm" />
                <input value={f.a} onChange={(e) => { const arr = [...form.faqs]; arr[i] = { ...arr[i], a: e.target.value }; setForm({ ...form, faqs: arr }); }} placeholder="Answer" className="px-2 py-1.5 rounded border border-emerald-200 text-sm" />
                <button type="button" onClick={() => setForm({ ...form, faqs: form.faqs.filter((_, j) => j !== i) })} className="text-rose-600 hover:bg-rose-50 rounded px-2 py-1 text-xs">Remove</button>
              </div>
            ))}
            <button type="button" onClick={() => setForm({ ...form, faqs: [...form.faqs, { q: '', a: '' }] })} className="text-xs text-emerald-700 hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Add FAQ</button>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-emerald-800">
          <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-emerald-700" />
          {form.published ? 'Publish live on the Blog & Guides section' : 'Save as draft (hidden from public)'}
        </label>

        {msg && <div className="text-sm text-emerald-800">{msg}</div>}

        <div className="flex items-center gap-2 pt-2">
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : editing ? 'Save Changes' : (form.published ? 'Publish Blog Live' : 'Save Draft')}
          </button>
          <button type="button" onClick={() => { setEditing(null); setView('list'); }} className="px-4 py-2.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50">Cancel</button>
        </div>
      </form>
    );
  }

  const copyApiExample = () => {
    const example = `curl -X POST "\${YOUR_API_URL}/api/blogs" \\
  -H "x-admin-key: <YOUR_ADMIN_API_KEY>" \\
  -H "Content-Type: application/json" \\
  -d '{"title":"My Automated Post","excerpt":"Short summary","category":"Sleep & Rest","categoryEmoji":"🌙","categories":["sleep"],"language":"English","readMinutes":3,"content":"## Heading\\n\\nParagraph one.","published":true}'`;
    try { navigator.clipboard.writeText(example); setCopiedKey(true); setTimeout(() => setCopiedKey(false), 2000); } catch (_e) { window.prompt('Copy:', example); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-emerald-900/70 text-sm">{blogs.length} blog post{blogs.length === 1 ? '' : 's'}</div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-xs text-emerald-700 hover:underline">Refresh</button>
          <button onClick={openNew} className="inline-flex items-center gap-2 px-3 py-2 rounded-full bg-emerald-700 text-white text-sm hover:bg-emerald-800">
            <Plus className="w-4 h-4" /> New Blog Post
          </button>
        </div>
      </div>

      <div className="mb-4 rounded-xl border border-emerald-100 bg-emerald-50/60 p-3 text-xs text-emerald-900">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="inline-flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-700" />
            <span>Automated Publishing API: <code className="bg-white px-1.5 py-0.5 rounded">POST /api/blogs</code> with header <code className="bg-white px-1.5 py-0.5 rounded">x-admin-key</code></span>
          </div>
          <button onClick={copyApiExample} className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
            {copiedKey ? <><Check className="w-3 h-3" /> Copied example</> : <><Copy className="w-3 h-3" /> Copy cURL example</>}
          </button>
        </div>
        <div className="mt-1 text-emerald-800/80">Set your API key in the backend env var <code>ADMIN_API_KEY</code>. Keep it private — anyone with it can publish blogs.</div>
      </div>

      <div className="rounded-xl border border-emerald-100 overflow-hidden bg-white">
        {loading ? <div className="p-6 text-sm text-emerald-700 text-center">Loading…</div> : blogs.length === 0 ? (
          <div className="p-8 text-center">
            <div className="text-sm text-emerald-800/70">No blog posts yet.</div>
            <button onClick={openNew} className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-emerald-700 text-white text-sm hover:bg-emerald-800"><Plus className="w-4 h-4" /> Write your first post</button>
          </div>
        ) : blogs.map((b) => (
          <div key={b.id} className="grid grid-cols-[60px_1fr_auto] md:grid-cols-[70px_1fr_120px_100px_150px] gap-3 items-center px-4 py-3 border-b border-emerald-50 last:border-b-0">
            {b.image ? <img src={b.image} alt="" className="w-14 h-10 md:w-16 md:h-11 rounded object-cover" onError={(e) => { e.target.style.display = 'none'; }} /> : <div className="w-14 h-10 md:w-16 md:h-11 rounded bg-emerald-100" />}
            <div>
              <div className="text-sm font-medium text-emerald-950 line-clamp-1">{b.title}</div>
              <div className="text-[11px] text-emerald-700 line-clamp-1">/{b.slug} · {b.language}</div>
            </div>
            <div className="hidden md:block text-xs text-emerald-800">{b.categoryEmoji} {b.category}</div>
            <div className="hidden md:block">
              <span className={`text-[10px] uppercase tracking-widest rounded-full px-2 py-1 ${b.published ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>{b.published ? 'Live' : 'Draft'}</span>
            </div>
            <div className="flex items-center gap-1 justify-end col-span-full md:col-span-1">
              <button onClick={() => togglePublished(b)} title={b.published ? 'Unpublish (draft)' : 'Publish live'} className={`p-1.5 rounded-full border ${b.published ? 'border-emerald-200 text-emerald-800 hover:bg-emerald-50' : 'bg-amber-100 border-amber-300 text-amber-700'}`}>
                {b.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              </button>
              <button onClick={() => openEdit(b)} title="Edit" className="p-1.5 rounded-full border border-emerald-200 text-emerald-800 hover:bg-emerald-50"><Edit3 className="w-4 h-4" /></button>
              <button onClick={() => remove(b)} title="Delete" className="p-1.5 rounded-full border border-rose-200 text-rose-700 hover:bg-rose-50"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BlogManager;
